/** Contact detail — v1's openContactView modal (Details / Comms / Files /
 * Props), rendered as a v2 drawer. Read-only: editing stays in v1 until the
 * contacts write door ships.
 *
 * v2 deltas, both BETTER and both honest:
 *  - Comms tab shows the contact's REAL threads (matched by phone last-10)
 *    instead of v1's hardcoded "No messages yet" placeholder.
 *  - The Messages stat tile counts those threads' messages (v1 shows 0). */
import { useEffect, useMemo, useState } from "react";
import type { Thread } from "../inbox/api";
import { clabel, contactDealAddresses, contactPhoneSet, C_PROF, C_SRC, type Contact } from "./api";

const TABS = ["details", "comms", "files", "props"] as const;
type Tab = (typeof TABS)[number];
const TAB_LABEL: Record<Tab, string> = { details: "Details", comms: "Comms", files: "Files", props: "Props" };

const fmtWhen = (t: number): string =>
  t ? new Date(t).toLocaleDateString([], { month: "short", day: "numeric" }) : "—";

export function ContactDrawer({
  contact,
  threads,
  dealAddresses,
  edits,
  onClose,
  onOpenThread,
  onEdit,
  onDelete,
}: {
  contact: Contact;
  threads: Thread[];
  dealAddresses: string[];
  edits: Map<string, Record<string, unknown>>;
  onClose: () => void;
  onOpenThread: ((peer: string) => void) | null;
  onEdit: ((c: Contact) => void) | null;
  onDelete: ((c: Contact) => void) | null;
}) {
  const [tab, setTab] = useState<Tab>("details");
  const c = contact;

  useEffect(() => {
    setTab("details");
  }, [c.natKey]);

  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  const myThreads = useMemo(() => {
    const phones = contactPhoneSet(c);
    if (phones.size === 0) return [];
    const last10 = (v: string) => v.replace(/[^0-9]/g, "").slice(-10);
    return threads.filter((t) => phones.has(last10(t.peer)));
  }, [c, threads]);

  const linkedDeals = useMemo(() => contactDealAddresses(c, dealAddresses, edits), [c, dealAddresses, edits]);
  const msgCount = myThreads.reduce((n, t) => n + t.count, 0);

  const initials = c.name.split(/\s+/).map((x) => x[0] ?? "").join("").slice(0, 2).toUpperCase() || "?";
  const workingArea = [c.workState, c.counties.join(", "), c.cities.join(", ")].filter(Boolean).join(" · ");

  const det = (label: string, value: string | null) =>
    value ? (
      <div className="cdet">
        <span className="lbl">{label}</span>
        <strong>{value}</strong>
      </div>
    ) : null;

  return (
    <>
      <div className="scrim" onClick={onClose} />
      <aside className="drawer">
        <header className="drawerhead">
          <div className="cdrawerwho">
            <span className="cavatar big">{initials}</span>
            <div>
              <h2>{c.name}</h2>
              <span className="sub">{c.company ?? c.type ?? ""}</span>
            </div>
          </div>
          <button className="close" onClick={onClose} title="Close (Esc)">×</button>
        </header>

        <div className="caltabs ctabs">
          {TABS.map((t) => (
            <button key={t} className={"caltab" + (tab === t ? " active" : "")} onClick={() => setTab(t)}>
              {TAB_LABEL[t]}
              {t === "comms" && myThreads.length ? <span className="ctabn">{myThreads.length}</span> : null}
              {t === "props" && linkedDeals.length ? <span className="ctabn">{linkedDeals.length}</span> : null}
            </button>
          ))}
        </div>

        {tab === "details" ? (
          <>
            <div className="cstats">
              <div className="cstat blue"><strong>{linkedDeals.length}</strong><span>Deals</span></div>
              <div className="cstat violet"><strong>{msgCount}</strong><span>Messages</span></div>
              <div className="cstat amber"><strong>0</strong><span>Files</span></div>
              <div className="cstat emerald"><strong>{c.rating || "—"}</strong><span>Rating</span></div>
            </div>

            <label className="lbl">Contact information</label>
            <div className="cdetgrid">
              {det("Company", c.company)}
              {det("Type", c.type)}
              {det("Proficiency", clabel(C_PROF, c.profCode))}
              {det("Lead Source", clabel(C_SRC, c.sourceCode) ?? c.sourceCode)}
              {det("Working Area", workingArea || null)}
              {det("Status", c.status)}
            </div>

            <label className="lbl">Contact details</label>
            <div className="clinks">
              {c.email ? <a href={`mailto:${c.email}`}>{c.email} <em>(Primary)</em></a> : null}
              {c.emailSecondary ? <a href={`mailto:${c.emailSecondary}`}>{c.emailSecondary} <em>(Secondary)</em></a> : null}
              {c.phone ? <a href={`tel:${c.phone}`}>{c.phone} <em>(Mobile)</em></a> : null}
              {c.phoneWork ? <a href={`tel:${c.phoneWork}`}>{c.phoneWork} <em>(Work)</em></a> : null}
              {c.phoneHome ? <a href={`tel:${c.phoneHome}`}>{c.phoneHome} <em>(Home)</em></a> : null}
              {c.website ? <a href={c.website} target="_blank" rel="noreferrer">{c.website}</a> : null}
              {c.linkedin ? <a href={c.linkedin} target="_blank" rel="noreferrer">{c.linkedin}</a> : null}
              {!c.email && !c.phone && !c.phoneWork && !c.phoneHome && !c.website ? <span className="sub">No contact details on file.</span> : null}
            </div>

            {c.tags.length ? (
              <div className="ctags">
                {c.tags.map((t) => (
                  <span key={t} className="ctag">{t}</span>
                ))}
              </div>
            ) : null}

            {c.notes ? <div className="cnotes">{c.notes}</div> : null}
          </>
        ) : null}

        {tab === "comms" ? (
          myThreads.length === 0 ? (
            <div className="empty">
              <strong>No messages yet</strong>
              <span>Calls and texts matched to this contact's phone numbers appear here.</span>
            </div>
          ) : (
            <div className="cthreads">
              {myThreads.map((t) => (
                <button
                  key={t.peer}
                  className="cthread"
                  onClick={() => onOpenThread?.(t.peer)}
                  title={onOpenThread ? "Open in Inbox" : undefined}
                >
                  <div className="cthreadtop">
                    <strong>{t.name ?? t.peer}</strong>
                    <span className="sub">{fmtWhen(t.lastAt)} · {t.count} msg{t.count === 1 ? "" : "s"}</span>
                  </div>
                  <span className={"cthreadlast" + (t.lastDir === "in" ? " in" : "")}>
                    {t.lastDir === "in" ? "← " : "→ "}
                    {t.lastBody || "(call)"}
                  </span>
                </button>
              ))}
            </div>
          )
        ) : null}

        {tab === "files" ? (
          <div className="empty">
            <strong>No files uploaded yet</strong>
            <span>Contracts, licenses and certifications live in v1 until the Drive backend connects here.</span>
          </div>
        ) : null}

        {tab === "props" ? (
          linkedDeals.length === 0 ? (
            <div className="empty">
              <strong>No deals linked</strong>
              <span>A deal links here automatically when a property with this contact becomes a transaction or wholesale deal.</span>
            </div>
          ) : (
            <div className="cdeals">
              {linkedDeals.map((a) => (
                <div key={a} className="cdeal">{a}</div>
              ))}
            </div>
          )
        ) : null}

        {onEdit || onDelete ? (
          <div className="cfoot actions">
            {onEdit ? <button className="btn" onClick={() => onEdit(c)}>Edit</button> : null}
            {onDelete ? <button className="btn danger" onClick={() => onDelete(c)}>Delete</button> : null}
          </div>
        ) : (
          <div className="cfoot sub">Editing this contact stays in v1 for now.</div>
        )}
      </aside>
    </>
  );
}
